import { apiErrorCode, apiErrorStatus } from '@/features/whatsapp/lib/whatsapp-errors';
import type { ApiError } from '@/shared/lib/problem-details';
import type { ConversationMember } from '../types/conversations.types';

/* ==========================================================================
   Códigos de erro do Atendimento

   O mesmo texto de `ConversationErrorCodes` na API (design D11 a D14). O
   painel classifica pelo `errorCode` do ProblemDetails, e não pelo status:
   o 409 serve a várias recusas diferentes.
   ========================================================================== */

export const CONVERSATION_ERRORS = {
  notFound: 'conversations.not_found',
  windowClosed: 'conversations.window_closed',
  modeConflict: 'conversations.mode_conflict',
  alreadyAssigned: 'conversations.already_assigned',
  idempotencyKeyReused: 'conversations.idempotency_key_reused',
  /** A loja não tem número conectado (o código do canal). */
  whatsappNotConnected: 'whatsapp.not_connected',
  /** O contato do canal não existe mais (o id pode ter mudado). */
  contactNotFound: 'whatsapp.contact_not_found',
} as const;

export { apiErrorCode, apiErrorStatus };

/** Quem já assumiu, lido da extensão `assignedTo` do 409 `conversations.already_assigned`. */
export function alreadyAssignedTo(error: unknown): ConversationMember | null {
  if (apiErrorCode(error) !== CONVERSATION_ERRORS.alreadyAssigned) return null;
  const assigned = (error as ApiError).problem.assignedTo as Partial<ConversationMember> | null | undefined;
  if (!assigned || typeof assigned.userId !== 'string') return null;
  return { userId: assigned.userId, name: typeof assigned.name === 'string' ? assigned.name : null };
}

/**
 * Erro sem resposta do servidor (rede caiu, tempo esgotado): o envio pode ter
 * chegado ou não, e só aqui a nova tentativa reaproveita a `Idempotency-Key`.
 */
export function isNetworkError(error: unknown): boolean {
  const status = apiErrorStatus(error);
  return status === undefined || status === 0;
}

/** A frase de uma recusa das ações, para mostrar ao lado do botão. */
export function actionErrorMessage(error: unknown): string {
  const code = apiErrorCode(error);
  switch (code) {
    case CONVERSATION_ERRORS.alreadyAssigned: {
      const name = alreadyAssignedTo(error)?.name;
      return `${name ?? 'Outra pessoa da equipe'} já assumiu esta conversa`;
    }
    case CONVERSATION_ERRORS.modeConflict:
    case CONVERSATION_ERRORS.windowClosed:
    case CONVERSATION_ERRORS.whatsappNotConnected: {
      const detail = (error as ApiError).problem.detail;
      return typeof detail === 'string' && detail ? detail : 'A conversa mudou. Confira o estado e tente de novo.';
    }
    case CONVERSATION_ERRORS.notFound:
    case CONVERSATION_ERRORS.contactNotFound:
      return 'Esta conversa não existe mais no Atendimento.';
    default:
      return isNetworkError(error)
        ? 'Sem conexão com o servidor. Tente de novo.'
        : 'Não foi possível concluir agora. Tente de novo.';
  }
}
